import * as THREE from 'three'

import { AIRCRAFT } from '../config/constants.js'
import { DEG, clamp, remap } from '../util/units.js'

/**
 * Procedural F-14B.
 *
 * Local frame — this is the contract the state quaternion assumes and
 * nothing here may break it:
 *   -Z  nose
 *   +X  right wing
 *   +Y  canopy
 *
 * Everything that moves gets its own pivot group so the animation is a single
 * rotation assignment rather than vertex work: wings sweep about Y at the
 * glove pivots, stabilators and the hook rotate about X, gear legs rotate about
 * X into their wells.
 *
 * Two empty Object3Ds — `eye` and `hookTip` — are the model's real interface to
 * the rest of the simulator. The ball is flown from `eye`; wire capture is
 * tested against `hookTip`.
 */

/* ===================================================================== *
 *  Reference geometry, shared with the physics side                     *
 *                                                                       *
 *  The arresting gear needs the hook point and the ball logic needs the  *
 *  pilot's eye, both at 120 Hz. Walking the scene graph for them would   *
 *  mean forcing a matrix update every physics step, so the same numbers  *
 *  the meshes are built from are exported and evaluated analytically.    *
 *  Change one, change the other — that is why they live together.        *
 * ===================================================================== */

/** Pilot design eye, model local frame, metres. */
export const EYE_LOCAL = new THREE.Vector3(0, 1.02, -7.05)

/**
 * Tailhook pivot, model local frame, metres.
 *
 * Positioned from the FDM rather than by eye: f-14b.xml puts the arrestor
 * force at structural station 700 in with the CG at 490 in, so the hook is
 * 210 in — 5.33 m — aft of the aircraft reference point. The pivot is set so
 * the fully-extended hook point lands there.
 *
 * This was 7.4 while the procedural airframe was about 22 m long instead of
 * the true 19.1. Once a correctly-scaled model went in, that put the wire
 * capture point twelve feet aft of where the flight model thinks the hook is
 * — about a third of a wire spacing of bias in every trap.
 */
export const HOOK_PIVOT_LOCAL = new THREE.Vector3(0, -0.45, 3.72)

/** Hook point relative to its pivot, stowed. */
export const HOOK_TIP_OFFSET = new THREE.Vector3(0, -0.14, 2.72)

/** Fully-extended hook angle, radians. */
/**
 * Main landing gear geometry, metres in the local frame.
 *
 * Exported because the number that matters — how far the wheels hang below the
 * aircraft's reference point — is what the trainer parks the jet at on the
 * deck, and that constant lives in config/constants.js. Two files, one
 * measurement; `npm test` checks they still agree.
 */
export const MAIN_GEAR = { pivotY: -0.5, legLength: 1.55, wheelRadius: 0.42 }

/** Metres from the reference point down to the bottom of the mains. */
export const MAIN_GEAR_DROP_M =
  -MAIN_GEAR.pivotY + MAIN_GEAR.legLength + MAIN_GEAR.wheelRadius

export const HOOK_MAX_ANGLE = 50 * DEG

/**
 * The opaque fuselage tub the cockpit sits in. Exported because it is forward
 * of and just below the pilot's eye, which makes it part of the over-the-nose
 * sightline — and therefore part of what `npm test` checks is not in the way.
 */
export const FUSELAGE_TUB = { name: 'fuselage tub', size: [1.0, 0.5, 3.6], pos: [0, 0.62, -6.4] }

/**
 * Hook point in the model's local frame for a given hook extension.
 * @param {number} hookNorm 0 stowed, 1 fully down
 */
export function hookTipLocal(hookNorm, out = new THREE.Vector3()) {
  const a = clamp(hookNorm, 0, 1) * HOOK_MAX_ANGLE
  const c = Math.cos(a)
  const s = Math.sin(a)
  const { y, z } = HOOK_TIP_OFFSET
  // Rotation about X, matching THREE.Object3D.rotation.x on the hook pivot.
  return out.set(
    HOOK_PIVOT_LOCAL.x,
    HOOK_PIVOT_LOCAL.y + (y * c - z * s),
    HOOK_PIVOT_LOCAL.z + (y * s + z * c),
  )
}

/** Build a thin flat panel from [spanwise, forward] points, thickness along Y. */
function flatPanel(points, thickness, taper = 1) {
  const shape = new THREE.Shape()
  points.forEach(([u, v], i) => (i === 0 ? shape.moveTo(u, v) : shape.lineTo(u, v)))
  shape.closePath()

  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: thickness,
    bevelEnabled: true,
    bevelThickness: thickness * 0.35,
    bevelSize: 0.06,
    bevelSegments: 1,
    curveSegments: 1,
  })
  // shape-x -> local X (span), shape-y -> local -Z, thickness -> local Y.
  geo.rotateX(-Math.PI / 2)
  geo.translate(0, -thickness / 2, 0)
  if (taper !== 1) geo.scale(1, taper, 1)
  geo.computeVertexNormals()
  return geo
}

const MAT = {
  topside: () =>
    new THREE.MeshStandardMaterial({ color: 0x8c949c, roughness: 0.62, metalness: 0.35 }),
  underside: () =>
    new THREE.MeshStandardMaterial({ color: 0xa9b1b8, roughness: 0.66, metalness: 0.3 }),
  dark: () => new THREE.MeshStandardMaterial({ color: 0x3c4348, roughness: 0.7, metalness: 0.4 }),
  radome: () =>
    new THREE.MeshStandardMaterial({ color: 0x5b6167, roughness: 0.78, metalness: 0.15 }),
  canopy: () =>
    new THREE.MeshPhysicalMaterial({
      color: 0x1a2830,
      roughness: 0.08,
      metalness: 0.0,
      transmission: 0.55,
      transparent: true,
      // The cockpit camera is inside this mesh and it is DoubleSide, so its
      // opacity tints the entire view. Anything above ~0.2 reads as looking
      // through smoked perspex rather than through a canopy.
      opacity: 0.16,
      side: THREE.DoubleSide,
    }),
  intake: () =>
    new THREE.MeshStandardMaterial({ color: 0x1a1e22, roughness: 0.95, metalness: 0.05 }),
  metal: () => new THREE.MeshStandardMaterial({ color: 0x6a7076, roughness: 0.4, metalness: 0.8 }),
  tyre: () => new THREE.MeshStandardMaterial({ color: 0x191b1d, roughness: 0.95 }),
  nozzle: () =>
    new THREE.MeshStandardMaterial({ color: 0x4a4237, roughness: 0.55, metalness: 0.75 }),
}

export class F14Model {
  constructor() {
    this.group = new THREE.Group()
    this.group.name = 'F-14B'

    this._materials = []

    this._buildFuselage()
    this._buildGloves()
    this._buildWings()
    this._buildTails()
    this._buildEngines()
    this._buildGear()
    this._buildHook()
    this._buildLights()
    this._buildReferencePoints()

    // Capture the procedural drawables NOW, while the group contains nothing
    // else. Later on the cockpit and any glTF airframe get parented to this
    // same group, and a traverse at that point would sweep them up too — which
    // is exactly how hiding "the procedural jet" once hid the downloaded model
    // along with it and left an empty sky. See setProceduralVisible().
    this._proceduralMeshes = []
    this._proceduralOn = true

    this.group.traverse((o) => {
      if (o.isMesh) {
        o.castShadow = true
        o.receiveShadow = true
      }
      if (o.isMesh || o.isSprite) this._proceduralMeshes.push(o)
    })

    this._proceduralGeometries = []
    this.group.traverse((o) => {
      if (o.geometry) this._proceduralGeometries.push(o.geometry)
    })
  }

  _mat(factory) {
    const m = factory()
    this._materials.push(m)
    return m
  }

  /* ------------------------------------------------------------------ */

  _buildFuselage() {
    // The F-14's planform: a slender chined forebody that widens into the flat
    // "pancake" carrying the engines. Getting the WIDTH distribution right is
    // most of the silhouette — the aircraft is far narrower at the cockpit
    // than a glance at a three-view suggests, and far wider aft.
    const half = [
      [0.0, 10.1],
      [0.30, 9.3],
      [0.55, 7.6],
      [0.72, 5.6],
      [0.86, 3.4],
      [1.02, 1.0],
      [1.34, -1.6],
      [1.95, -3.8],
      [2.42, -5.6],
      [2.48, -7.8],
      [2.05, -9.3],
      [0.0, -9.5],
    ]
    const pts = [...half, ...half.slice(0, -1).reverse().map(([x, f]) => [-x, f])]

    // The aft body is a flat slab; the forebody is not, so they are built
    // separately. One uniform extrusion over the whole planform is what made
    // the jet read as a cut-out rather than an aeroplane.
    const body = new THREE.Mesh(flatPanel(pts, 1.28), this._mat(MAT.topside))
    body.name = 'Fuselage'
    this.group.add(body)

    // Forebody: a tapering spine sitting on the planform, so the nose section
    // has depth and a ridge line instead of being a plate.
    const spine = [
      { z: -9.6, w: 0.34, h: 0.62, y: 0.10 },
      { z: -8.4, w: 0.62, h: 0.86, y: 0.14 },
      { z: -7.0, w: 0.92, h: 1.02, y: 0.16 },
      { z: -5.4, w: 1.24, h: 1.06, y: 0.12 },
      { z: -3.6, w: 1.60, h: 0.98, y: 0.06 },
    ]
    for (const seg of spine) {
      const m = new THREE.Mesh(
        new THREE.BoxGeometry(seg.w, seg.h, 1.7),
        this._mat(MAT.topside),
      )
      m.position.set(0, seg.y, seg.z)
      this.group.add(m)
    }

    // Radome: a lathe rather than a cone, so it has the long ogive curve the
    // Tomcat's nose actually has.
    const noseProfile = []
    for (let i = 0; i <= 12; i++) {
      const t = i / 12
      // Ogive: radius falls off as a power curve toward the tip.
      const r = 0.40 * Math.pow(1 - t, 0.62)
      noseProfile.push(new THREE.Vector2(Math.max(r, 0.004), t * 2.9))
    }
    const nose = new THREE.Mesh(
      new THREE.LatheGeometry(noseProfile, 20),
      this._mat(MAT.radome),
    )
    nose.rotation.x = Math.PI / 2
    nose.position.set(0, 0.12, -9.8)
    this.group.add(nose)

    // Pitot boom.
    const boom = new THREE.Mesh(
      new THREE.CylinderGeometry(0.022, 0.012, 0.7, 6),
      this._mat(MAT.dark),
    )
    boom.rotation.x = Math.PI / 2
    boom.position.set(0, 0.12, -13.0)
    this.group.add(boom)

    // Cockpit tub and canopy. Two-seat, so the glass is long.
    const tub = new THREE.Mesh(
      new THREE.BoxGeometry(FUSELAGE_TUB.size[0], FUSELAGE_TUB.size[1], FUSELAGE_TUB.size[2]),
      this._mat(MAT.dark),
    )
    tub.position.set(FUSELAGE_TUB.pos[0], FUSELAGE_TUB.pos[1], FUSELAGE_TUB.pos[2])
    this.group.add(tub)

    const canopy = new THREE.Mesh(new THREE.SphereGeometry(0.72, 20, 14), this._mat(MAT.canopy))
    canopy.scale.set(0.8, 0.74, 2.6)
    canopy.position.set(0, 0.88, -6.45)
    this.group.add(canopy)
    this.canopy = canopy

    // NOTE: the windscreen bow and canopy frame are built by Cockpit.js, not
    // here. Rendering them in both places doubles the geometry in the cockpit
    // view, and the frame only exists to be looked at from inside.

    // The pancake between the nacelles — the flat lifting body that gives the
    // F-14 its distinctive plan and a good deal of its lift.
    const pancake = new THREE.Mesh(
      new THREE.BoxGeometry(2.35, 0.5, 6.6),
      this._mat(MAT.underside),
    )
    pancake.position.set(0, -0.42, 2.6)
    this.group.add(pancake)

    // Beavertail fairing aft between the nozzles.
    const beaver = new THREE.Mesh(
      new THREE.BoxGeometry(1.5, 0.42, 2.6),
      this._mat(MAT.topside),
    )
    beaver.position.set(0, 0.05, 8.3)
    this.group.add(beaver)
  }

  _buildGloves() {
    // Fixed inboard wing sections that carry the pivots.
    const right = [
      [1.0, 2.2],
      [2.45, -0.2],
      [2.45, -3.1],
      [1.0, -2.6],
    ]
    const left = right.map(([x, f]) => [-x, f])

    for (const pts of [right, left]) {
      const g = new THREE.Mesh(flatPanel(pts, 0.42), this._mat(MAT.topside))
      g.position.y = 0.16
      this.group.add(g)
    }
  }

  _buildWings() {
    this.wingPivots = []

    // Panel measured from its own pivot: [spanwise, forward]
    const panel = [
      [0.0, 0.35],
      [7.55, -1.35],
      [7.55, -2.5],
      [0.0, -2.9],
    ]

    for (const side of [1, -1]) {
      const pivot = new THREE.Group()
      pivot.position.set(side * 2.25, 0.18, -0.55)
      this.group.add(pivot)

      const pts = side === 1 ? panel : panel.map(([u, v]) => [-u, v])
      const wing = new THREE.Mesh(flatPanel(pts, 0.3), this._mat(MAT.topside))
      pivot.add(wing)

      // Flaps and spoilers hinge about their own leading edges, not about the
      // wing-sweep pivot, so their panel points are authored relative to the
      // hinge line and the pivot group is translated onto it. Rotating a
      // surface about a pivot 2 m away from its hinge swings it bodily off the
      // wing instead of deflecting it.
      const FLAP_HINGE_Z = 2.2
      const flapPts = (
        side === 1
          ? [
              [0.4, -2.35],
              [6.9, -2.05],
              [6.9, -2.55],
              [0.4, -2.95],
            ]
          : [
              [-0.4, -2.35],
              [-6.9, -2.05],
              [-6.9, -2.55],
              [-0.4, -2.95],
            ]
      ).map(([u, v]) => [u, v + FLAP_HINGE_Z])

      const flapPivot = new THREE.Group()
      flapPivot.position.z = FLAP_HINGE_Z
      pivot.add(flapPivot)
      const flap = new THREE.Mesh(flatPanel(flapPts, 0.18), this._mat(MAT.underside))
      flapPivot.add(flap)

      // Spoilers on the upper surface: the F-14's roll control at low sweep,
      // and the DLC surfaces on approach.
      const SPOILER_HINGE_Z = 1.5
      const spoilerPts = (
        side === 1
          ? [
              [1.2, -1.5],
              [5.6, -1.5],
              [5.6, -2.1],
              [1.2, -2.2],
            ]
          : [
              [-1.2, -1.5],
              [-5.6, -1.5],
              [-5.6, -2.1],
              [-1.2, -2.2],
            ]
      ).map(([u, v]) => [u, v + SPOILER_HINGE_Z])

      const spoilerPivot = new THREE.Group()
      spoilerPivot.position.set(0, 0.16, SPOILER_HINGE_Z)
      pivot.add(spoilerPivot)
      const spoiler = new THREE.Mesh(flatPanel(spoilerPts, 0.08), this._mat(MAT.dark))
      spoilerPivot.add(spoiler)

      this.wingPivots.push({ side, pivot, flapPivot, spoilerPivot })
    }
  }

  _buildTails() {
    // Twin fins, canted outboard.
    this.fins = []
    for (const side of [1, -1]) {
      // Leading edge swept about 48 degrees, trailing edge nearly upright,
      // squared tip with the ECM fairing. Proportion matters more than detail
      // here: the twin fins are the first thing that says "Tomcat" from the
      // side, and a plain triangle says "generic jet".
      const finShape = new THREE.Shape()
      finShape.moveTo(0.0, 0.0)
      finShape.lineTo(1.45, 3.28)
      finShape.lineTo(2.28, 3.28)
      finShape.lineTo(2.52, 2.85)
      finShape.lineTo(2.96, 0.0)
      finShape.closePath()

      const geo = new THREE.ExtrudeGeometry(finShape, {
        depth: 0.16,
        bevelEnabled: true,
        bevelThickness: 0.05,
        bevelSize: 0.04,
        bevelSegments: 1,
        curveSegments: 1,
      })
      geo.translate(-1.4, 0, -0.08)
      geo.rotateY(Math.PI / 2)

      const fin = new THREE.Mesh(geo, this._mat(MAT.topside))
      fin.position.set(side * 1.42, 0.5, 5.0)
      fin.rotation.z = -side * 7 * DEG
      this.group.add(fin)
      this.fins.push(fin)

      // Ventral fin under each nacelle.
      const vShape = new THREE.Shape()
      vShape.moveTo(0, 0)
      vShape.lineTo(0.1, -1.0)
      vShape.lineTo(1.1, -1.0)
      vShape.lineTo(1.4, 0)
      vShape.closePath()
      const vGeo = new THREE.ExtrudeGeometry(vShape, { depth: 0.1, bevelEnabled: false })
      vGeo.rotateY(Math.PI / 2)
      const vent = new THREE.Mesh(vGeo, this._mat(MAT.dark))
      vent.position.set(side * 1.42, -0.52, 7.0)
      this.group.add(vent)
    }

    // Stabilators. One-piece slabs, so they are also the roll control at high
    // sweep — the pivot carries both the symmetric and differential motion.
    this.stabPivots = []
    for (const side of [1, -1]) {
      const pivot = new THREE.Group()
      pivot.position.set(side * 1.1, 0.05, 6.4)
      this.group.add(pivot)

      const pts =
        side === 1
          ? [
              [0.0, 0.9],
              [3.5, -0.5],
              [3.5, -1.35],
              [0.0, -1.7],
            ]
          : [
              [0.0, 0.9],
              [-3.5, -0.5],
              [-3.5, -1.35],
              [0.0, -1.7],
            ]
      const stab = new THREE.Mesh(flatPanel(pts, 0.22), this._mat(MAT.topside))
      pivot.add(stab)
      this.stabPivots.push({ side, pivot })
    }
  }

  _buildEngines() {
    this.nozzles = []
    this.burners = []

    const burnerTex = this._burnerTexture()

    for (const side of [1, -1]) {
      const nacelle = new THREE.Mesh(
        new THREE.CylinderGeometry(0.8, 0.76, 11.0, 18, 1, true),
        this._mat(MAT.topside),
      )
      nacelle.rotation.x = Math.PI / 2
      nacelle.scale.set(1.0, 1.0, 0.92) // slightly flattened, as they are
      nacelle.position.set(side * 1.42, -0.12, 3.0)
      this.group.add(nacelle)

      // Rectangular intake standing off the fuselage on a splitter plate. The
      // gap between duct and body is one of the most recognisable things about
      // the aircraft from any front quarter, and a box flush to the fuselage
      // throws it away.
      const duct = new THREE.Mesh(new THREE.BoxGeometry(0.95, 1.2, 3.4), this._mat(MAT.topside))
      duct.position.set(side * 1.42, -0.06, -3.5)
      duct.rotation.x = -0.03
      this.group.add(duct)

      // The dark inlet face itself, inset so it reads as an opening.
      const mouth = new THREE.Mesh(
        new THREE.BoxGeometry(0.8, 1.02, 0.12),
        this._mat(MAT.intake),
      )
      mouth.position.set(side * 1.42, -0.06, -5.16)
      this.group.add(mouth)

      // Splitter plate bridging duct to fuselage.
      const splitter = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, 1.05, 3.0),
        this._mat(MAT.dark),
      )
      splitter.position.set(side * 0.95, -0.06, -3.6)
      this.group.add(splitter)

      // Nozzle.
      const nozzle = new THREE.Mesh(
        new THREE.CylinderGeometry(0.62, 0.74, 1.3, 18, 1, true),
        this._mat(MAT.nozzle),
      )
      nozzle.rotation.x = Math.PI / 2
      nozzle.position.set(side * 1.42, -0.12, 8.7)
      this.group.add(nozzle)
      this.nozzles.push(nozzle)

      // Afterburner plume.
      const burner = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: burnerTex,
          transparent: true,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
          opacity: 0,
        }),
      )
      burner.position.set(side * 1.42, -0.12, 10.6)
      burner.scale.set(1.6, 4.4, 1)
      this.group.add(burner)
      this.burners.push(burner)
    }
  }

  _burnerTexture() {
    const c = document.createElement('canvas')
    c.width = 64
    c.height = 128
    const ctx = c.getContext('2d')
    const g = ctx.createLinearGradient(0, 0, 0, 128)
    g.addColorStop(0.0, 'rgba(255,255,255,0.95)')
    g.addColorStop(0.18, 'rgba(255,214,150,0.85)')
    g.addColorStop(0.45, 'rgba(255,132,60,0.55)')
    g.addColorStop(0.75, 'rgba(140,80,255,0.22)')
    g.addColorStop(1.0, 'rgba(0,0,0,0)')
    ctx.fillStyle = g
    // Taper the plume to a point.
    ctx.beginPath()
    ctx.moveTo(10, 0)
    ctx.lineTo(54, 0)
    ctx.lineTo(38, 128)
    ctx.lineTo(26, 128)
    ctx.closePath()
    ctx.fill()
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return tex
  }

  _buildGear() {
    this.gearLegs = []

    const strut = (len, r = 0.075) =>
      new THREE.Mesh(new THREE.CylinderGeometry(r, r, len, 8), this._mat(MAT.metal))
    const wheel = (r, w) => {
      const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, w, 14), this._mat(MAT.tyre))
      m.rotation.z = Math.PI / 2
      return m
    }

    // Nose gear: forward-retracting, twin wheel, and it holds the launch bar.
    // Strut lengths are set so the wheels sit where JSBSim's contact points
    // are — 89 in below the CG at the nose, 94 in at the mains. If those two
    // disagree the jet visibly floats above or sinks into the deck.
    const nosePivot = new THREE.Group()
    nosePivot.position.set(0, -0.45, -6.9)
    this.group.add(nosePivot)
    {
      const leg = strut(1.5, 0.085)
      leg.position.y = -0.75
      nosePivot.add(leg)
      const axle = new THREE.Group()
      axle.position.y = -1.5
      nosePivot.add(axle)
      for (const x of [-0.17, 0.17]) {
        const w = wheel(0.3, 0.17)
        w.position.x = x
        axle.add(w)
      }
      // Launch bar.
      const bar = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.08, 1.1), this._mat(MAT.metal))
      bar.position.set(0, -1.4, -0.6)
      nosePivot.add(bar)
    }
    this.gearLegs.push({ pivot: nosePivot, retractAxis: 'x', retractAngle: 1.62 })

    // Mains: retract forward into the nacelles.
    for (const side of [1, -1]) {
      const pivot = new THREE.Group()
      pivot.position.set(side * 1.28, MAIN_GEAR.pivotY, 0.5)
      this.group.add(pivot)

      const leg = strut(MAIN_GEAR.legLength, 0.095)
      leg.position.y = -MAIN_GEAR.legLength / 2
      pivot.add(leg)

      const w = wheel(MAIN_GEAR.wheelRadius, 0.24)
      w.position.y = -MAIN_GEAR.legLength
      pivot.add(w)

      // Drag brace.
      const brace = new THREE.Mesh(new THREE.BoxGeometry(0.07, 1.5, 0.07), this._mat(MAT.metal))
      brace.position.set(side * -0.22, -0.85, 0.35)
      brace.rotation.z = side * 0.16
      brace.rotation.x = -0.22
      pivot.add(brace)

      this.gearLegs.push({ pivot, retractAxis: 'x', retractAngle: 1.55 })
    }
  }

  _buildHook() {
    this.hookPivot = new THREE.Group()
    this.hookPivot.position.copy(HOOK_PIVOT_LOCAL)
    this.group.add(this.hookPivot)

    const shank = new THREE.Mesh(
      new THREE.CylinderGeometry(0.075, 0.06, 2.6, 8),
      this._mat(MAT.metal),
    )
    shank.rotation.x = Math.PI / 2
    shank.position.z = 1.3
    this.hookPivot.add(shank)

    const point = new THREE.Mesh(new THREE.TorusGeometry(0.17, 0.06, 6, 12, Math.PI * 1.3), this._mat(MAT.dark))
    point.rotation.y = Math.PI / 2
    point.rotation.z = Math.PI * 0.35
    point.position.set(0, -0.1, 2.6)
    this.hookPivot.add(point)

    // The point the wire-capture test uses. Kept as its own object so the
    // physics never has to guess where the hook ended up after animation.
    this.hookTip = new THREE.Object3D()
    this.hookTip.position.copy(HOOK_TIP_OFFSET)
    this.hookPivot.add(this.hookTip)
  }

  _buildLights() {
    const lamp = (color, size = 0.1) =>
      new THREE.Mesh(
        new THREE.SphereGeometry(size, 6, 5),
        new THREE.MeshBasicMaterial({ color }),
      )

    this.navLights = []
    // Wingtip lights ride on the wing pivots so they sweep with the panels:
    // green on the right, red on the left, exactly as on the jet.
    for (const wp of this.wingPivots) {
      // +Z is aft in the model frame; the wing panel's tip chord sits at
      // z ~ +1.4 to +2.5, so the light belongs at a positive z.
      const l = lamp(wp.side === 1 ? 0x22ff55 : 0xff2a2a, 0.09)
      l.position.set(wp.side * 7.5, 0.05, 1.9)
      wp.pivot.add(l)
      this.navLights.push(l)
    }
    const tail = lamp(0xffffff, 0.08)
    tail.position.set(0, 1.0, 8.6)
    this.group.add(tail)
    this.navLights.push(tail)

    // Approach light on the nose gear door: the thing the LSO reads your AoA
    // from at night. Colour is driven by the indexer state.
    this.approachLight = lamp(0xffb02e, 0.11)
    this.approachLight.position.set(0, -0.5, -7.6)
    this.group.add(this.approachLight)
  }

  _buildReferencePoints() {
    // Pilot design eye. Everything about the ball is measured from here.
    this.eye = new THREE.Object3D()
    this.eye.position.copy(EYE_LOCAL)
    this.group.add(this.eye)

    // Chase camera anchor.
    this.chaseAnchor = new THREE.Object3D()
    this.chaseAnchor.position.set(0, 1.6, 12.0)
    this.group.add(this.chaseAnchor)
  }

  /* ------------------------------------------------------------------ */

  /**
   * Drive every animated surface from one frame of aircraft state.
   * @param {import('../rails/RailsFlight.js').RailState} s
   * @param {number} dt
   */
  sync(s, dt) {
    // ---- wing sweep ----------------------------------------------------
    const sweepRad = (s.sweepDeg - AIRCRAFT.SWEEP_MIN_DEG) * DEG
    for (const wp of this.wingPivots) {
      // Right wing sweeps aft with a negative Y rotation, left with positive.
      wp.pivot.rotation.y = -wp.side * sweepRad

      // Rotation about +X moves a point aft of the pivot DOWN for a positive
      // angle (y' = y·cos - z·sin), which is the same convention the tailhook
      // uses. So flaps take a positive angle and spoilers a negative one.
      wp.flapPivot.rotation.x = s.flapNorm * 35 * DEG

      // Spoilers rise for roll and for DLC.
      const rollSpoiler = wp.side === 1 ? Math.max(0, -s.aileronDeg) : Math.max(0, s.aileronDeg)
      const spoilerDeg = clamp(rollSpoiler * 1.6 + s.dlcNorm * 18, 0, 55)
      wp.spoilerPivot.rotation.x = -spoilerDeg * DEG
    }

    // ---- stabilators ----------------------------------------------------
    // Symmetric pitch plus a differential component that grows with sweep,
    // mirroring the way the real jet hands roll over to the tail.
    const diffGain = remap(s.sweepNorm, 0.5, 1.0, 0.25, 1.0)
    for (const sp of this.stabPivots) {
      const differential = -sp.side * s.aileronDeg * 0.45 * diffGain
      sp.pivot.rotation.x = (s.elevatorDeg + differential) * DEG
    }

    // ---- rudders --------------------------------------------------------
    // Both fins deflect together. Only the trailing half of each fin is a
    // rudder on the real jet, so the visible throw is scaled down.
    for (const fin of this.fins) {
      fin.rotation.y = -s.rudderDeg * 0.5 * DEG
    }

    // ---- gear -----------------------------------------------------------
    const gearDown = clamp(s.gearNorm, 0, 1)
    for (const leg of this.gearLegs) {
      leg.pivot.rotation.x = (1 - gearDown) * leg.retractAngle
      leg.pivot.visible = gearDown > 0.02
    }

    // ---- hook -----------------------------------------------------------
    // Uses the same constant the wire-capture maths does.
    this.hookPivot.rotation.x = clamp(s.hookNorm, 0, 1) * HOOK_MAX_ANGLE

    // ---- afterburner ----------------------------------------------------
    const abTarget = s.augmenting ? 1 : 0
    for (const b of this.burners) {
      const m = b.material
      m.opacity += (abTarget - m.opacity) * Math.min(1, dt * 9)
      // `&& this._proceduralOn` is not belt and braces. sync() runs every
      // frame and would otherwise resurrect this sprite the moment the burners
      // light, long after setProceduralVisible(false) hid the jet it belongs
      // to — leaving two flames burning in empty air behind the glTF model.
      b.visible = m.opacity > 0.02 && this._proceduralOn
      // Flicker so it does not read as a decal.
      const flick = 0.9 + Math.sin(performance.now() * 0.045) * 0.06
      b.scale.set(1.5 * flick, (3.4 + 1.6 * m.opacity) * flick, 1)
    }

    // ---- approach indexer light ----------------------------------------
    const err = s.aoaUnits - AIRCRAFT.ONSPEED_AOA_UNITS
    const color =
      Math.abs(err) <= AIRCRAFT.AOA_DONUT_BAND ? 0xffb02e : err > 0 ? 0xff3b30 : 0x22ff55
    this.approachLight.material.color.setHex(color)
    // Same trap as the burners, and a worse one to look at: an orange ball
    // hanging where the nose gear door used to be, dead centre of the chase
    // view, on every approach.
    this.approachLight.visible = s.gearNorm > 0.5 && this._proceduralOn
  }

  /**
   * Hide or show the procedural geometry.
   *
   * Used when a glTF airframe takes over. Two rules, both learned the hard way:
   *
   *   1. It walks the list captured in the constructor, NOT the live group.
   *      By the time this is called the group also holds the cockpit and the
   *      downloaded airframe, and traversing it would hide the very model we
   *      switched to — the aircraft simply vanishes from the chase view.
   *   2. Only drawables are touched. `eye`, `hookTip` and `chaseAnchor` are
   *      bare Object3Ds and must stay live, because the cameras read them and
   *      — more importantly — they define where the ball is flown from and
   *      where the wire is caught. Those two points are the contract between
   *      the model and the simulator, and swapping the artwork must not move
   *      them.
   */
  setProceduralVisible(visible) {
    this._proceduralOn = visible
    for (const o of this._proceduralMeshes) o.visible = visible
  }

  /** World-space pilot eye position. */
  getEyeWorld(out = new THREE.Vector3()) {
    return this.eye.getWorldPosition(out)
  }

  /** World-space hook point — what the wires are tested against. */
  getHookTipWorld(out = new THREE.Vector3()) {
    return this.hookTip.getWorldPosition(out)
  }

  dispose() {
    // Same reasoning as setProceduralVisible: the glTF airframe and the
    // cockpit are children of this group but are owned by ModelOverrides, which
    // disposes them itself. Double-disposing a GPU resource is not fatal in
    // three.js, but it is a lie about ownership and it hides real leaks.
    this._proceduralGeometries.forEach((g) => g.dispose())
    this._materials.forEach((m) => m.dispose())
  }
}
